import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Megaphone,
  Calendar,
  Building2,
  Download,
  Upload,
  CheckCircle,
  Loader2,
  FileText,
  Clock,
  Ban
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export default function ExpertOpportunities() {
  const { token, API } = useAuth();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [disabledMsg, setDisabledMsg] = useState('');

  const fetchOpps = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/announcements/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOpps(data.announcements || []);
        setDisabledMsg(data.module_disabled ? data.message : '');
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchOpps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const downloadAnnouncementPdf = async (annId, filename) => {
    try {
      const res = await fetch(`${API}/announcements/${annId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'opportunity.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const submitProposal = async (annId, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    setUploadingId(annId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/announcements/${annId}/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (res.ok) {
        toast.success('Proposal submitted! Confirmation email sent.');
        fetchOpps();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Submission failed');
      }
    } catch (e) {
      toast.error('Submission failed');
    } finally {
      setUploadingId(null);
    }
  };

  const daysUntil = (deadline) => {
    if (!deadline) return null;
    const d = deadline.includes('T') ? new Date(deadline) : new Date(deadline + 'T23:59:59Z');
    const days = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (opps.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>No open opportunities right now.</p>
        <p className="text-xs mt-1">You&apos;ll be notified by email when new ones match your expertise.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {opps.map((opp) => {
        const days = daysUntil(opp.deadline);
        const submitted = !!opp.my_submission;
        return (
          <Card key={opp.id} className="overflow-hidden" data-testid={`opp-card-${opp.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <h4 className="font-semibold text-lg">{opp.title}</h4>
                {submitted ? (
                  <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                    <CheckCircle className="w-3 h-3 mr-1" /> Submitted
                  </Badge>
                ) : days !== null && days <= 1 ? (
                  <Badge variant="destructive">
                    <Clock className="w-3 h-3 mr-1" />
                    {days === 0 ? 'Last day!' : 'Tomorrow!'}
                  </Badge>
                ) : days !== null ? (
                  <Badge variant="secondary">
                    <Clock className="w-3 h-3 mr-1" />
                    {days} day{days !== 1 ? 's' : ''} left
                  </Badge>
                ) : null}
              </div>

              <p className="text-sm text-muted-foreground mb-3 whitespace-pre-wrap">{opp.description}</p>

              <div className="grid sm:grid-cols-2 gap-3 text-xs text-muted-foreground mb-4">
                <div className="flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  Sponsor: <span className="font-medium text-foreground">{opp.sponsor_institution}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Deadline: <span className="font-medium text-foreground">{opp.deadline}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-3 border-t">
                {opp.pdf_file_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadAnnouncementPdf(opp.id, opp.pdf_filename)}
                    className="gap-2"
                    data-testid={`download-call-${opp.id}`}
                  >
                    <Download className="w-4 h-4" />
                    Download Call PDF
                  </Button>
                )}
                {disabledMsg ? (
                  <Button
                    size="sm"
                    variant={submitted ? 'outline' : 'default'}
                    className="gap-2"
                    onClick={() => toast.error(`${disabledMsg} Submissions are unavailable.`)}
                    data-testid={`submit-proposal-disabled-${opp.id}`}
                  >
                    <Ban className="w-4 h-4" />
                    {submitted ? 'Replace Proposal' : 'Submit Proposal (PDF)'}
                  </Button>
                ) : submitted ? (
                  <label className="inline-flex">
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="gap-2 cursor-pointer"
                      disabled={uploadingId === opp.id}
                    >
                      <span>
                        {uploadingId === opp.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Replace Proposal
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => submitProposal(opp.id, e.target.files?.[0])}
                      data-testid={`replace-proposal-${opp.id}`}
                    />
                  </label>
                ) : (
                  <label className="inline-flex">
                    <Button
                      asChild
                      size="sm"
                      className="gap-2 cursor-pointer"
                      disabled={uploadingId === opp.id}
                    >
                      <span>
                        {uploadingId === opp.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Submit Proposal (PDF)
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => submitProposal(opp.id, e.target.files?.[0])}
                      data-testid={`submit-proposal-${opp.id}`}
                    />
                  </label>
                )}
                {submitted && (
                  <span className="text-xs text-muted-foreground self-center flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {opp.my_submission.pdf_filename}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
